import { gql, useMutation, useQuery } from '@apollo/client'

import {
  UpdateUserDeliveryInfoMutation,
  UpdateUserDeliveryInfoMutationVariables,
  UserDeliveryInfoFragmentDoc,
  UserDeliveryInfoQuery,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages } = useRouter()
  const { analytics } = useAnalytics()
  const { data } = useQuery<UserDeliveryInfoQuery>(QUERY)
  const [updateUserDeliveryInfo] = useMutation<
    UpdateUserDeliveryInfoMutation,
    UpdateUserDeliveryInfoMutationVariables
  >(UPDATE_USER_NOTIFICATION_SETTINGS)

  return {
    state: {
      user: data?.user ?? null,
    },
    actions: {
      onSubmit: async values => {
        await updateUserDeliveryInfo({
          variables: {
            updateUserDeliveryInfoInputs: {
              fullName: values.fullName,
              phoneNumber: values.phoneNumber,
              address: {
                postalCode: values.postalCode,
                prefecture: values.prefecture,
                city: values.city,
                line1: values.line1,
                line2: values.line2,
              },
            },
          },
          update(cache, { data }) {
            const userDeliveryInfoResponse = data?.updateUserDeliveryInfo
            const existingUser = cache.readQuery<UserDeliveryInfoQuery>({
              query: QUERY,
            })
            if (existingUser && userDeliveryInfoResponse) {
              cache.writeQuery({
                query: QUERY,
                data: {
                  user: {
                    ...existingUser.user,
                    ...userDeliveryInfoResponse,
                  },
                },
              })
            }
          },
        }).then(() => {
          analytics?.track('registerShippingInfo', {
            applicationKey: config.applicationKey,
            type: data?.user?.address?.fullAddress ? 'update' : 'register',
          })
        })
        await router.goBack(routerPages.AccountSetting)
        return
      },
    },
  }
})

const QUERY = gql`
  query userDeliveryInfo {
    user {
      ...UserDeliveryInfo
    }
  }
  ${UserDeliveryInfoFragmentDoc}
`

const UPDATE_USER_NOTIFICATION_SETTINGS = gql`
  mutation updateUserDeliveryInfo(
    $updateUserDeliveryInfoInputs: UserDeliveryInput!
  ) {
    updateUserDeliveryInfo(inputs: $updateUserDeliveryInfoInputs) {
      ...UserDeliveryInfo
    }
  }
  ${UserDeliveryInfoFragmentDoc}
`
